import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLink } from "shared/components/common/base-link";
import { notEmpty } from "shared/lib/utils";

import { checkHasLiveTag } from "scmp-app/components/article/article-render/helpers";
import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { ExclusiveFlag } from "scmp-app/components/content/content-item-render/exclusive-flag";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { checkHasSeriesShortLabel } from "scmp-app/components/content/content-series-short-label/helpers";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePgType, usePlusQueryParameters } from "scmp-app/components/content/helpers";
import { pathname as plusPathname } from "scmp-app/pages/plus";
import type { homeSecondaryV2ContentItemContent$key } from "scmp-app/queries/__generated__/homeSecondaryV2ContentItemContent.graphql";

import {
  ActionBar,
  Container,
  CoverImage,
  Header,
  Headline,
  LiveTagContainer,
  PlusTag,
  SponsorTagContainer,
  StyledEntityLink,
  SubHeadline,
  Summary,
  TopicLabel,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  currentTopicReference?: string;
  hideHeadlineTag?: boolean;
  isReadingTimeEnabled?: boolean;
  reference: homeSecondaryV2ContentItemContent$key;
  skipHighlight?: boolean;
  withBookmark?: boolean;
  withCarousel?: boolean;
  withComment?: boolean;
  withExclusiveHeaderFlag?: boolean;
  withImage?: boolean;
  withLiveTag?: boolean;
  withSection?: boolean;
  withSeriesShortLabel?: boolean;
  withSubHeadline?: boolean;
  withSummary?: boolean;
  withTopic?: boolean;
  withVideoPlayIcon?: boolean;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  currentTopicReference,
  hideHeadlineTag = false,
  isReadingTimeEnabled = false,
  onClick,
  ref,
  reference,
  skipHighlight = false,
  topicLinkVariant,
  withBookmark = false,
  withCarousel = false,
  withComment = false,
  withExclusiveHeaderFlag = false,
  withImage = false,
  withLiveTag = false,
  withSection = false,
  withSeriesShortLabel = false,
  withSubHeadline = false,
  withSummary = false,
  withTopic = false,
  withVideoPlayIcon = false,
}) => {
  const content = useFragment(
    graphql`
      fragment homeSecondaryV2ContentItemContent on Content {
        entityId
        urlAlias
        flag
        ...entityLink
        ...helpersCheckHasLiveTagArticle
        ...helpersCheckHasSeriesShortLabelContent
        ...helpersCheckIsPlusArticle
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withActionBar: true
            withCoverImage: true
            withCoverImageLeadingSlides: true
            withCoverImageSize1200x800: true
            withCoverImageSize540x360: true
            withHeadline: true
            withLiveTag: true
            withSections: true
            withSeriesShortLabel: true
            withSponsorTag: true
            withSubHeadline: true
            withSummary: true
            withTopicLink: true
            withVideoDurationOverlay: true
          )
      }
    `,
    reference,
  );

  const providers = useContentItemProviders(content, topicLinkVariant);
  const hasLiveTag = checkHasLiveTag(content);
  const isShowLiveTag = hasLiveTag && withLiveTag;
  const isPlusArticle = checkIsPlusArticle(content);
  const hasSeriesShortLabel = checkHasSeriesShortLabel(content);

  const isExclusiveHeaderFlag = withExclusiveHeaderFlag && content.flag === "EXCLUSIVE";

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);

  const pgtype = usePgType();
  const plusQueryParameters = usePlusQueryParameters(content);

  const renderHeader = () => {
    if (isShowLiveTag) {
      return <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>;
    }
    if (isExclusiveHeaderFlag) {
      return <ExclusiveFlag />;
    }
    if (withTopic) {
      return (
        <TopicLabel>
          {providers.topicLink?.({
            currentTopicReference,
            preferShortName: true,
          })}
        </TopicLabel>
      );
    }
    if (withSection) {
      return (
        <>
          {withSeriesShortLabel && hasSeriesShortLabel
            ? providers.seriesShortLabel?.()
            : providers.sections()}
        </>
      );
    }
  };

  return (
    <Container className={className} ref={ref}>
      <Header>
        {renderHeader()}
        {isPlusArticle && (
          <BaseLink isAnchor pathname={plusPathname} query={{ module: "plus_label", pgtype }}>
            <PlusTag aria-label="Plus" role="img" />
          </BaseLink>
        )}
      </Header>

      <Headline>
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          query={plusQueryParameters}
          reference={content}
        >
          {providers.headline({
            hideTag: hideHeadlineTag || isExclusiveHeaderFlag,
            preferSocialHeadline: true,
            skipHighlight,
          })}
        </StyledEntityLink>
      </Headline>

      {withSummary && (
        <Summary>
          {providers.summary({
            isTextOverflowEllipsis: true,
          })}
        </Summary>
      )}
      {withSubHeadline && !isSponsorArticle && <SubHeadline>{providers.subHeadline()}</SubHeadline>}

      {isSponsorArticle ? (
        <SponsorTagContainer>{providers.sponsorTag?.({ variant: "home" })}</SponsorTagContainer>
      ) : (
        <ActionBar>
          {providers.actionBar({
            commentIconPosition: "left",
            displayDateVariant: isReadingTimeEnabled ? "readingTime" : "updatedDate",
            isBookmarkVisible: withBookmark ?? false,
            isCommentsVisible: withComment ?? false,
            isIconVisible: false,
          })}
        </ActionBar>
      )}

      {withImage && (
        <CoverImage>
          <StyledEntityLink
            onClick={() => onClick?.(content.entityId, content.urlAlias)}
            query={plusQueryParameters}
            reference={content}
          >
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size1200x800",
              },
              withCarousel,
            })}
            {withVideoPlayIcon && providers.video?.videoPlayIcon()}
            {providers.video?.durationOverlay({
              iconSize: "hidden",
              variant: "homepage",
            })}
          </StyledEntityLink>
        </CoverImage>
      )}
    </Container>
  );
};

Component.displayName = "ContentItemHomeSecondaryV2";

export const ContentItemHomeSecondaryV2 = withHighlightedHeadline(Component);
